import React, { useState, useEffect, useRef, useCallback } from 'react'
import { toast } from 'react-toastify'

import AutocompleteCityDestination from '../../Autocomplete/AutocompleteCityDestination'
import TypeSelector from './TypeSelector'

function DestinationFilters(props) {
	const {
		filters,
		handleFilterChange,
		handleDestinationChange,
		labels,
		styles,
		isDarkMode,
		onDropoffLocationsChange, // New prop for real-time updates
		destinationType,
		setDestinationType,
	} = props

	const [locations, setLocations] = useState([])
	const containerRef = useRef(null)

	// Load locations from localStorage on component mount
	useEffect(() => {
		try {
			const stored = localStorage.getItem('destinationLocations')
			if (stored) {
				const storedLocations = JSON.parse(stored)

				const recentLocations = storedLocations
					.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
					.slice(0, 10)

				const locationsWithSelection = recentLocations.map(loc => ({
					...loc,
					isSelected: false,
				}))
				setLocations(locationsWithSelection)

				if (recentLocations.length !== storedLocations.length) {
					localStorage.setItem(
						'destinationLocations',
						JSON.stringify(recentLocations)
					)
				}
			}
		} catch (error) {
			console.error('Error loading destination locations:', error)
			setLocations([])
		}
	}, [])

	// Sync local state when filter state changes.
	// Match by name (stable across browsers); merge in any filter locations not yet in
	// local state so saved searches applied on a different browser still populate the field.
	useEffect(() => {
		setLocations(prev => {
			const filterNames = new Set(
				filters.dropoffLocations.map(loc => loc.name)
			)

			const updated = prev.map(loc => ({
				...loc,
				isSelected: filterNames.has(loc.name),
			}))

			const existingNames = new Set(updated.map(l => l.name))
			const missing = filters.dropoffLocations
				.filter(loc => loc.name && !existingNames.has(loc.name))
				.map(loc => ({
					id: loc.id || `${loc.name}|${loc.lat}|${loc.lng}`,
					name: loc.name,
					lat: loc.lat,
					lng: loc.lng,
					timestamp: new Date().toISOString(),
					isSelected: true,
				}))

			if (missing.length === 0) {
				const hasChanges = updated.some(
					(loc, index) => loc.isSelected !== prev[index].isSelected
				)
				return hasChanges ? updated : prev
			}

			return [...missing, ...updated].slice(0, 10)
		})
	}, [filters.dropoffLocations])

	// Save locations to localStorage when locations change (but don't trigger filter updates)
	useEffect(() => {
		if (locations.length > 0) {
			// Remove isSelected before saving to localStorage
			const locationsToSave = locations.map(({ isSelected, ...rest }) => rest)
			localStorage.setItem(
				'destinationLocations',
				JSON.stringify(locationsToSave)
			)
		}
	}, [locations])

	// Helper function to notify parent of location changes (call explicitly, not in useEffect)
	const notifyParentOfLocationChange = useCallback((newLocations) => {
		if (onDropoffLocationsChange) {
			const selectedLocs = newLocations.filter(loc => loc.isSelected)
			const dropoffLocations = selectedLocs.map(city => ({
				id: city.id,
				name: city.name,
				lat: city.lat,
				lng: city.lng,
			}))
			onDropoffLocationsChange(dropoffLocations)
		}
	}, [onDropoffLocationsChange])

	const addLocationAndToggle = useCallback(cityData => {
		const locationName = `${cityData.city}, ${cityData.state_id}`

		setLocations(prev => {
			const existingIndex = prev.findIndex(loc => loc.name === locationName)

			if (existingIndex >= 0) {
				const existingLocation = prev[existingIndex]

				if (
					!existingLocation.isSelected &&
					prev.filter(loc => loc.isSelected).length >= 3
				) {
					toast.warning('Maximum 3 destination locations allowed')
					return prev
				}

				const updated = [...prev]
				updated[existingIndex] = {
					...updated[existingIndex],
					isSelected: !updated[existingIndex].isSelected,
					timestamp: new Date().toISOString(),
				}
				
				// Notify parent of the change
				notifyParentOfLocationChange(updated)
				return updated
			} else {
				if (prev.filter(loc => loc.isSelected).length >= 3) {
					toast.warning('Maximum 3 destination locations allowed')
					return prev
				}

				const newLocation = {
					id: locationName,
					name: locationName,
					lat: cityData.lat || 0,
					lng: cityData.lng || 0,
					isSelected: true, // This is only in state, not saved to localStorage
					timestamp: new Date().toISOString(),
				}
				const newList = [newLocation, ...prev]
				const finalList = newList.length > 10 ? newList.slice(0, 10) : newList
				
				// Notify parent of the change
				notifyParentOfLocationChange(finalList)
				return finalList
			}
		})
	}, [notifyParentOfLocationChange])

	const handleLabelClick = useCallback(e => {
		const container = e.target.closest('.input-container')
		if (container) {
			const inputElement = container.querySelector('input')
			if (inputElement) {
				inputElement.focus()
			}
		}
	}, [])

	const inputStyles = {
		...styles,
		height: '40px',
		boxSizing: 'border-box',
		borderTopLeftRadius: '0',
		borderBottomLeftRadius: '0',
		borderTopRightRadius: '6px',
		borderBottomRightRadius: '6px',
	}

	const focusRingClass =
		'focus:ring focus:ring-opacity-50 focus:border-blue-300 focus:ring-blue-200'

	const inputClassName = `input input-bordered join-item shrink mt-1 p-2 w-full ${focusRingClass}`

	const selectClassName = `border join-item mt-1 py-2 rounded-lg w-full h-[40px] ${focusRingClass} ${
		isDarkMode
			? 'bg-gray-800 border-gray-600 text-white'
			: 'bg-white border-gray-300 text-gray-900'
	}`

	return (
		<div className="join flex flex-1">
			<TypeSelector
				destinationType={destinationType}
				setDestinationType={setDestinationType}
				labels={labels}
				styles={styles}
				isDarkMode={isDarkMode}
			/>

			<div className="input-container flex-grow">
				<div ref={containerRef}>
					<AutocompleteCityDestination
						destination={filters.destination}
						style={inputStyles}
						className={`${inputClassName} join-item z-60`}
						name="destination"
						handleDestinationChange={handleDestinationChange}
						isDarkMode={isDarkMode}
						locations={locations}
						onToggleSelection={addLocationAndToggle}
					/>

					<label
						htmlFor="destination-input"
						className={
							filters.destination?.city ||
							locations.filter(loc => loc.isSelected).length > 0
								? 'filled'
								: ''
						}
						style={{
							...labels,
							backgroundColor: isDarkMode ? '#1c2126' : '#ffffff',
							color: isDarkMode ? '#C9D1D9' : '#3a4353',
						}}
						onClick={handleLabelClick}
					>
						Destination{' '}
						<span style={{ fontSize: '0.99em', opacity: 0.7 }}>
							(up to 3)
						</span>
					</label>
				</div>
			</div>

			{/* Destination Radius */}
			<div className="radius-select select-wrap">
				<label
					style={{
						...labels,
						backgroundColor: isDarkMode ? '#1c2126' : '#ffffff',
						color: isDarkMode ? '#C9D1D9' : '#3a4353',
					}}
				>
					Radius
				</label>
				<select
					name="dropoffMaxDist"
					onChange={handleFilterChange}
					className={`${selectClassName} join-item`}
					style={styles}
					value={filters.dropoffMaxDist}
					disabled={!filters.dropCity && !locations.some(loc => loc.isSelected)}
				>
					<option value={10}>10 mi</option>
					<option value={25}>25 mi</option>
					<option value={50}>50 mi</option>
					<option value={75}>75 mi</option>
					<option value={100}>100 mi</option>
					<option value={125}>125 mi</option>
					<option value={150}>150 mi</option>
					<option value={200}>200 mi</option>
					<option value={250}>250 mi</option>
				</select>
			</div>
		</div>
	)
}

export default DestinationFilters
